import { z } from "zod";
import { useHistory } from "react-router-dom";
import {
  useForm,
  Controller,
  useWatch,
  useFieldArray,
  UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button-tw";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  CalendarIcon,
  Check,
  ChevronDownIcon,
  ChevronsUpDown,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  InvoiceDeliveryMethod,
  InvoiceFrequency,
  InvoicePaymentTerms,
  InvoiceToSchema,
} from "@/queries/invoice/types";
import { cn } from "@/utils/classnames";
import { useInvoices } from "@/hooks/invoice/use-invoices";
import { useContacts } from "@/hooks/contact/use-contacts";
import { getContactName, getContactSearchValue } from "@/utils/contact";
import { CreateContactDrawer } from "@/components/contact/create-contact-drawer";
import { useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addDays, format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { invoiceQueryKeys } from "@/queries/invoice";
import { useAuth } from "@/providers/auth";
import { sendInvoiceToCustomer } from "@/api/v1-jwt/invoice/invoice";
import { toast } from "react-toastify";
import {
  CreateInvoiceFormData,
  CreateInvoiceFormSchema,
} from "@/components/invoices/create-invoice-form/schema";
import { useInvoiceSummary } from "@/components/invoices/create-invoice-form/use-invoice-summary";
import NumberInput from "@/components/form/number-input";
import IntegerInput from "@/components/form/integer-input";
import { IframeInvoicePreview } from "@/components/invoices/create-invoice-form/iframe-invoice-preview";
import { useCreateInvoiceContext } from "@/components/invoices/create-invoice-form/create-invoice-context";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useUpdateInvoice } from "@/hooks/invoice/use-update-invoice";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { useClientSettings } from "@/queries/client-settings/use-client-settings";
import { SettingKey } from "@/queries/client-settings/types";
import { Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

// Form component
interface EditInvoiceFormProps {
  invoiceId: string;
  initialData: any;
  onFormDataChange?: (formData: CreateInvoiceFormData) => void;
}

export const EditInvoiceForm: React.FC<EditInvoiceFormProps> = ({
  invoiceId,
  initialData,
  onFormDataChange,
}) => {
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const queryClient = useQueryClient();
  const history = useHistory();
  const updateInvoiceMutation = useUpdateInvoice();
  const { data: existingInvoices } = useInvoices();
  const { data: contacts } = useContacts();
  const { data: tenancy } = useTenancy();
  const ccProviderStatus = (tenancy as any)?.flags
    ?.credit_card_provider_application_status;
  const hasCCFeature =
    !!(tenancy as any)?.feature_flags?.credit_card_payment &&
    (ccProviderStatus === "APPLICATION_APPROVED" ||
      (import.meta.env.MODE !== 'production' && ccProviderStatus === "APPLICATION_SUBMITTED"));

  const { data: integrationSettings } = useClientSettings(
    { userId: userId || "", category: "integrations" },
    { enabled: !!userId && hasCCFeature },
  );
  const ccDefaultSetting = integrationSettings?.find(
    (s) => s.key === SettingKey.DELUXE_INVOICE_DEFAULT_CC,
  );
  const [shouldSendInvoice, setShouldSendInvoice] = React.useState(false);
  const [addContact, setAddContact] = React.useState(false);
  const [taxToggle, setTaxToggle] = React.useState<"percentage" | "amount">(
    () => {
      if (initialData?.invoice_info?.invoice_tax_percentage > 0) {
        return "percentage";
      }
      if (initialData?.invoice_info?.invoice_tax_amount > 0) {
        return "amount";
      }
      return "percentage";
    },
  );
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [isSmallScreen] = useMediaQuery("(max-width: 1559px)");

  // Track the original invoice status to prevent draft updates on sent invoices
  const originalInvoiceStatus = initialData?.invoice_info?.invoice_status;
  const isSentInvoice =
    originalInvoiceStatus && originalInvoiceStatus !== "draft";

  // Convert invoice data to form format
  const convertInvoiceToFormData = (invoice: any): CreateInvoiceFormData => {
    const invoiceInfo = invoice.invoice_info;

    return {
      status: invoiceInfo.invoice_status === "draft" ? "draft" : "send",
      deliveryMethod:
        invoiceInfo.invoice_status === "draft"
          ? null
          : InvoiceDeliveryMethod.EMAIL,
      invoiceFrequency:
        invoiceInfo.invoice_frequency || InvoiceFrequency.ONE_TIME,
      invoiceFrequencyInterval: 0,
      invoiceExternalId: invoiceInfo.invoice_external_id || "",
      invoiceExternalIdLink: invoiceInfo.invoice_external_id_link || "",
      invoiceNumber: invoiceInfo.invoice_number || "",
      terms:
        !invoiceInfo.invoice_payment_terms ||
        invoiceInfo.invoice_payment_terms === "none"
          ? undefined
          : invoiceInfo.invoice_payment_terms,
      dueDate:
        invoiceInfo.invoice_due_date &&
        (!invoiceInfo.invoice_payment_terms ||
          invoiceInfo.invoice_payment_terms === "none")
          ? new Date(invoiceInfo.invoice_due_date)
          : undefined,
      sendDate: invoiceInfo.invoice_send_date
        ? new Date(invoiceInfo.invoice_send_date)
        : undefined,
      notes: invoiceInfo.invoice_notes || "",
      taxPercentage: invoiceInfo.invoice_tax_percentage ?? 0,
      taxAmount:
        (invoiceInfo.invoice_tax_percentage ?? 0) !== 0
          ? 0
          : (invoiceInfo.invoice_tax_amount ?? 0),
      shippingFee: invoiceInfo.invoice_shipping_fee || 0,
      invoiceTo: invoiceInfo.invoice_to,
      lineItems: invoiceInfo.invoice_line_items?.map((item: any) => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        item_total: item.item_total,
      })) || [{ description: "", quantity: 1, unit_price: 0, item_total: 0 }],
      enableCardPayment: invoiceInfo.allow_credit_card_payment,
    };
  };

  const form = useForm<CreateInvoiceFormData>({
    resolver: zodResolver(
      CreateInvoiceFormSchema.superRefine((data, ctx) => {
        // Unique invoice number validation (exclude current invoice)
        if (
          data.invoiceNumber &&
          // @ts-ignore
          existingInvoices.some(
            (invoice) =>
              invoice.invoice_info.invoice_number === data.invoiceNumber &&
              invoice.invoice_id !== invoiceId,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["invoiceNumber"],
            message: "This number already exists, please try another one",
          });
        }
      }),
    ),
    defaultValues: convertInvoiceToFormData(initialData),
  });

  const summary = useInvoiceSummary(form);

  const invoiceFrequency = form.watch("invoiceFrequency");
  const lineItems = form.watch("lineItems");
  const formValues = form.watch();
  const { isPreviewOpen, setIsPreviewOpen } = useCreateInvoiceContext();

  // Track form changes and notify parent - use ref to avoid unnecessary re-renders
  const previousFormValues = React.useRef<CreateInvoiceFormData | null>(null);

  React.useEffect(() => {
    if (
      onFormDataChange &&
      JSON.stringify(formValues) !== JSON.stringify(previousFormValues.current)
    ) {
      previousFormValues.current = formValues;
      onFormDataChange(formValues);
    }
  }, [formValues, onFormDataChange]);

  // Auto-adjust delivery method if current selection becomes unavailable
  React.useEffect(() => {
    const contactPhone = formValues.invoiceTo?.phone;
    const hasPhoneNumber = contactPhone && contactPhone.trim().length > 0;
    const currentDeliveryMethod = formValues.deliveryMethod;

    // If no phone number and current method requires SMS, switch to email
    if (
      !hasPhoneNumber &&
      currentDeliveryMethod &&
      (currentDeliveryMethod === InvoiceDeliveryMethod.SMS ||
        currentDeliveryMethod === InvoiceDeliveryMethod.ALL)
    ) {
      form.setValue("deliveryMethod", InvoiceDeliveryMethod.EMAIL);
    }
  }, [formValues.invoiceTo?.phone, formValues.deliveryMethod, form]);

  React.useEffect(() => {
    if (
      ccDefaultSetting !== undefined &&
      formValues.enableCardPayment === undefined
    ) {
      form.setValue(
        "enableCardPayment",
        ccDefaultSetting?.value?.deluxe_invoice_default_cc ?? false,
      );
    }
  }, [ccDefaultSetting, form]);

  // Handle line item changes
  const updateLineItemTotal = (index: number) => {
    const item = lineItems[index];
    const quantity = item?.quantity || 0;
    const unitPrice = item?.unit_price || 0;
    const itemTotal = quantity * unitPrice;

    form.setValue(`lineItems.${index}.item_total`, itemTotal);
  };

  // Update all line item totals when lineItems change
  React.useEffect(() => {
    lineItems.forEach((item, index) => {
      const expectedTotal = (item?.quantity || 0) * (item?.unit_price || 0);
      if (item?.item_total !== expectedTotal) {
        updateLineItemTotal(index);
      }
    });
  }, [lineItems]);

  const {
    fields: lineItemsFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });

  const onFormSubmit = async (data: CreateInvoiceFormData) => {
    if (data.status === "send") {
      setShouldSendInvoice(true);
    } else {
      // UPDATE DRAFT INVOICE
      const invoiceData = summary.getPayloadData();
      updateInvoiceMutation.mutate(
        //@ts-ignore
        {
          id: invoiceId,
          ...invoiceData,
        },
        {
          onSuccess: () => {
            toast.success("Your invoice has been updated.");
            queryClient.invalidateQueries(invoiceQueryKeys.all);
            history.push("/invoices");
          },
        },
      );
    }
  };

  // Handle submit with error scrolling
  const handleFormSubmit = async (data: CreateInvoiceFormData) => {
    onFormSubmit(data);
  };

  // Combobox state
  const [open, setOpen] = useState(false);

  const [zoomLevel] = useState(1);
  return (
    <>
      {/* Drawer Preview for small screens */}
      {isSmallScreen && isPreviewOpen && (
        <Drawer
          key="invoice-preview-drawer"
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
        >
          <DrawerContent className="h-[92vh]">
            <DrawerHeader>
              <DrawerTitle>Invoice Preview</DrawerTitle>
              <DrawerDescription>
                Preview of your invoice as it will appear to customers
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-hidden rounded-lg bg-gray-100 h-full mx-4 mb-4">
              <div className="relative px-4 py-5 sm:p-6 h-full">
                <IframeInvoicePreview
                  formData={formValues}
                  zoomLevel={zoomLevel}
                  showZoomControls={false}
                />
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}

      <main className={`flex-1 ${isSmallScreen ? "col-span-5" : "col-span-3"}`}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="flex flex-col gap-4 md:gap-6"
        >
          <div className="divide-y divide-gray-200 overflow-hidden md:rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <p>Invoice Information</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {/* Contact Selection */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Billing To
                  </label>
                  <button
                    type="button"
                    className="flex justify-between items-center text-sm font-medium text-skin-primary"
                    onClick={() => setAddContact(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Contact
                  </button>
                </div>
                <Controller
                  name="invoiceTo"
                  control={form.control}
                  render={({ field }) => (
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild ref={field.ref}>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={open}
                          className={cn(
                            "w-full justify-between h-20 hover:bg-gray-50",
                            {
                              "border-l-4 border-l-skin-primary bg-gray-50":
                                field.value,
                            },
                          )}
                        >
                          {field.value?.contact_id ? (
                            <div className="flex flex-col items-start">
                              <span className="truncate text-base">
                                {getContactName({
                                  contact_id: field.value.contact_id,
                                  contact_info: field.value,
                                })}
                              </span>
                              <span className="text-sm text-gray-500 truncate">
                                {` ${field.value?.email || ""}`}
                              </span>
                            </div>
                          ) : (
                            "Select contact..."
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 popover-content-width-same-as-its-trigger max-h-[300px] overflow-y-auto">
                        <Command>
                          <CommandInput placeholder="Search contact..." />
                          <CommandEmpty>No contact found.</CommandEmpty>
                          <CommandGroup>
                            {/* @ts-ignore */}
                            {contacts.map((contact) => (
                              <CommandItem
                                key={contact.contact_id}
                                value={getContactSearchValue(contact)}
                                onSelect={() => {
                                  const selected = InvoiceToSchema.parse({
                                    ...contact.contact_info,
                                    contact_id: contact.contact_id,
                                  });

                                  field.onChange(selected);
                                  setOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.value?.contact_id ===
                                      contact?.contact_id
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span className="text-nowrap">
                                  {getContactName(contact)}
                                </span>
                                {contact?.contact_info?.email ? (
                                  <span className="text-xs text-gray-500 ml-3 truncate">
                                    {` ${contact.contact_info?.email || ""}`}
                                  </span>
                                ) : (
                                  <span className="text-xs text-red-500 ml-3">
                                    You can’t select this contact. Please add an
                                    email address to continue.
                                  </span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                />
                {form.formState.errors.invoiceTo && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.invoiceTo.email?.message ||
                      "Select a contact"}
                  </p>
                )}
              </div>

              {/* Invoice Type and Send Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <Controller
                    control={form.control}
                    name="invoiceFrequency"
                    render={({ field }) => (
                      <Select
                        onValueChange={(value) => {
                          form.setValue("sendDate", undefined);
                          field.onChange(value);
                        }}
                        defaultValue={field.value}
                      >
                        <SelectTrigger ref={field.ref}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value={InvoiceFrequency.ONE_TIME}
                            className="focus:border-l-2 py-3.5"
                          >
                            One Time
                          </SelectItem>
                          <SelectItem
                            value={InvoiceFrequency.SCHEDULED}
                            className="focus:border-l-2 py-3.5"
                          >
                            Scheduled
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {form.formState.errors.invoiceFrequency && (
                    <p className="text-red-500 text-sm">
                      {form.formState.errors.invoiceFrequency.message}
                    </p>
                  )}
                </div>

                {invoiceFrequency === "scheduled" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send Date
                    </label>
                    <Controller
                      control={form.control}
                      name="sendDate"
                      render={({ field }) => {
                        const tomorrow = addDays(new Date(), 1);
                        return (
                          <Popover>
                            <PopoverTrigger asChild ref={field.ref}>
                              <Button
                                variant="outline"
                                className={`w-full justify-start text-left font-normal ${
                                  !field.value && "text-muted-foreground"
                                }`}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                fromDate={tomorrow}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        );
                      }}
                    />
                    {form.formState.errors.sendDate && (
                      <p className="text-red-500 text-sm">
                        {form.formState.errors.sendDate.message}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Terms */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms or Custom Due Date
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                  <div>
                    <Controller
                      control={form.control}
                      name="terms"
                      render={({ field }) => {
                        const dueDate = useWatch({
                          control: form.control,
                          name: "dueDate",
                        });

                        const terms = useWatch({
                          control: form.control,
                          name: "terms",
                        });
                        const isDisabled = (!!dueDate && !terms) || !!terms;
                        return (
                          <div className="relative">
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value);
                                if (value) {
                                  form.setValue("dueDate", undefined);
                                }
                              }}
                              value={terms || ""}
                              disabled={isDisabled}
                            >
                              <SelectTrigger
                                ref={field.ref}
                                className={`w-full ${terms ? "pr-10" : ""}`}
                              >
                                <SelectValue placeholder="Payment Terms" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem
                                  value={InvoicePaymentTerms.NET_15}
                                  className="focus:border-l-2 py-2"
                                >
                                  Net 15
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.NET_30}
                                  className="focus:border-l-2 py-2"
                                >
                                  Net 30
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.NET_45}
                                  className="focus:border-l-2 py-2"
                                >
                                  Net 45
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.NET_60}
                                  className="focus:border-l-2 py-2"
                                >
                                  Net 60
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.NET_90}
                                  className="focus:border-l-2 py-2"
                                >
                                  Net 90
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.DUE_ON_ISSUE}
                                  className="focus:border-l-2 py-2"
                                >
                                  Due On Issue
                                </SelectItem>
                                <SelectItem
                                  value={InvoicePaymentTerms.CASH_ON_DELIVERY}
                                  className="focus:border-l-2 py-2"
                                >
                                  Cash On Delivery
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {terms && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                onClick={() => {
                                  form.setValue("dueDate", undefined);
                                  form.setValue("terms", undefined);
                                }}
                              >
                                <X className="h-4 w-4 text-gray-500" />
                              </Button>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>

                  <div>
                    <Controller
                      control={form.control}
                      name="dueDate"
                      render={({ field }) => {
                        const terms = useWatch({
                          control: form.control,
                          name: "terms",
                        });
                        const dueDateValue = useWatch({
                          control: form.control,
                          name: "dueDate",
                        });
                        const invoiceFrequency = useWatch({
                          control: form.control,
                          name: "invoiceFrequency",
                        });
                        const isDisabled = !!terms || !!dueDateValue;
                        const minDate =
                          invoiceFrequency === InvoiceFrequency.SCHEDULED
                            ? addDays(new Date(), 1)
                            : new Date();

                        return (
                          <div className="relative">
                            <Popover>
                              <PopoverTrigger
                                asChild
                                disabled={isDisabled}
                                ref={field.ref}
                              >
                                <Button
                                  variant="outline"
                                  className={`w-full justify-start text-left font-normal ${
                                    !dueDateValue && "text-muted-foreground"
                                  } ${dueDateValue ? "pr-10" : ""}`}
                                  disabled={isDisabled}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {dueDateValue ? (
                                    format(dueDateValue, "PPP")
                                  ) : (
                                    <span>Due Date</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={dueDateValue}
                                  onSelect={(date) => {
                                    form.setValue("dueDate", date, {
                                      shouldValidate: true,
                                    });
                                  }}
                                  fromDate={minDate}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            {dueDateValue && !terms && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                onClick={() => {
                                  form.setValue("dueDate", undefined);
                                  form.setValue("terms", undefined);
                                }}
                              >
                                <X className="h-4 w-4 text-gray-500" />
                              </Button>
                            )}
                          </div>
                        );
                      }}
                    />
                    {form.formState.errors.dueDate && (
                      <p className="text-red-500 text-sm">
                        {form.formState.errors.dueDate.message}
                      </p>
                    )}
                  </div>
                </div>
                {form.formState.errors.terms && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.terms.message}
                  </p>
                )}
              </div>

              {/* Invoice Number and Reference */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Number
                  </label>
                  <Input {...form.register("invoiceNumber")} />
                  {form.formState.errors.invoiceNumber && (
                    <p className="text-red-500 text-sm">
                      {form.formState.errors.invoiceNumber.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Number
                  </label>
                  <Input {...form.register("invoiceExternalId")} />
                  {form.formState.errors.invoiceExternalId && (
                    <p className="text-red-500 text-sm">
                      {form.formState.errors.invoiceExternalId.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Reference Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Link
                </label>
                <Input {...form.register("invoiceExternalIdLink")} />
                {form.formState.errors.invoiceExternalIdLink && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.invoiceExternalIdLink.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div className="divide-y divide-gray-200 overflow-hidden md:rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <p>Invoice Items</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Line Items
                </label>
                <div className="border rounded-md">
                  {/* Desktop table header */}
                  <div className="hidden md:grid grid-cols-[3fr_1fr_1fr_40px] gap-4 p-4 bg-gray-50 border-b text-sm font-medium text-gray-700">
                    <div>Item</div>
                    <div>Qty</div>
                    <div>Unit Price</div>
                    <div></div>
                  </div>
                  {lineItemsFields.map((item, index) => (
                    <div
                      key={item.id}
                      className="p-4 items-center border-b last:border-b-0"
                    >
                      <div className="flex flex-col">
                        {/* Desktop layout */}
                        <div className="hidden md:grid grid-cols-[3fr_1fr_1fr_40px] gap-4">
                          <div>
                            <Controller
                              control={form.control}
                              name={`lineItems.${index}.description`}
                              render={({ field }) => (
                                <Input
                                  ref={field.ref}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Enter item description"
                                  className="border-gray-300"
                                />
                              )}
                            />
                          </div>
                          <div>
                            <Controller
                              control={form.control}
                              name={`lineItems.${index}.quantity`}
                              render={({ field }) => {
                                return (
                                  <IntegerInput
                                    ref={field.ref}
                                    min={0}
                                    value={field.value}
                                    onChange={(value: number | undefined) => {
                                      field.onChange(value);
                                      updateLineItemTotal(index);
                                    }}
                                  />
                                );
                              }}
                            />
                          </div>
                          <div>
                            <Controller
                              control={form.control}
                              name={`lineItems.${index}.unit_price`}
                              render={({ field }) => {
                                return (
                                  <NumberInput
                                    ref={field.ref}
                                    value={field.value}
                                    onChange={(value) => {
                                      field.onChange(value);
                                      updateLineItemTotal(index);
                                    }}
                                    type="currency"
                                  />
                                );
                              }}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Mobile card layout */}
                        <div className="md:hidden space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                              Item #{index + 1}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => remove(index)}
                              className="text-gray-500 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <Controller
                              control={form.control}
                              name={`lineItems.${index}.description`}
                              render={({ field }) => (
                                <Input
                                  ref={field.ref}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Enter item description"
                                  className="border-gray-300"
                                />
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Quantity
                              </label>
                              <Controller
                                control={form.control}
                                name={`lineItems.${index}.quantity`}
                                render={({ field }) => {
                                  return (
                                    <IntegerInput
                                      ref={field.ref}
                                      min={0}
                                      value={field.value}
                                      onChange={(value: number | undefined) => {
                                        field.onChange(value);
                                        updateLineItemTotal(index);
                                      }}
                                    />
                                  );
                                }}
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Unit Price
                              </label>
                              <Controller
                                control={form.control}
                                name={`lineItems.${index}.unit_price`}
                                render={({ field }) => {
                                  return (
                                    <NumberInput
                                      ref={field.ref}
                                      value={field.value}
                                      onChange={(value) => {
                                        field.onChange(value);
                                        updateLineItemTotal(index);
                                      }}
                                      type="currency"
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {form.formState.errors.lineItems?.[index]
                          ?.description && (
                          <p className="text-red-500 text-sm">
                            {
                              form.formState.errors?.lineItems?.[index]
                                .description.message
                            }
                          </p>
                        )}
                        {form.formState.errors.lineItems?.[index]?.quantity && (
                          <p className="text-red-500 text-sm">
                            {
                              form.formState.errors?.lineItems?.[index].quantity
                                .message
                            }
                          </p>
                        )}
                        {form.formState.errors.lineItems?.[index]
                          ?.unit_price && (
                          <p className="text-red-500 text-sm">
                            {
                              form.formState.errors?.lineItems?.[index]
                                .unit_price.message
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {form.formState.errors.lineItems?.root && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors?.lineItems?.root.message}
                  </p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    append({
                      description: "",
                      quantity: 1,
                      unit_price: 0,
                      item_total: 0,
                    })
                  }
                  className="mt-2 text-skin-primary hover:text-skin-primary/75 hover:bg-transparent pl-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Line
                </Button>
                {form.formState.errors.lineItems && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.lineItems.message}
                  </p>
                )}
              </div>

              {/* Shipping and Tax */}
              <div className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Shipping Fee
                    </label>
                    <Controller
                      control={form.control}
                      name="shippingFee"
                      render={({ field }) => {
                        return (
                          <NumberInput
                            ref={field.ref}
                            value={field.value}
                            onChange={field.onChange}
                            type="currency"
                          />
                        );
                      }}
                    />
                  </div>
                  <div className="">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 ">
                        Tax
                      </label>
                      <div className="bg-input/50 inline-flex h-7 rounded-full p-0.5">
                        <RadioGroup
                          defaultValue={taxToggle}
                          onValueChange={(value: "percentage" | "amount") => {
                            form.setValue("taxAmount", 0);
                            form.setValue("taxPercentage", 0);

                            setTaxToggle(value);
                          }}
                          value={taxToggle}
                          className="group after:bg-background has-focus-visible:after:border-ring has-focus-visible:after:ring-ring/50 relative inline-grid grid-cols-[1fr_1fr] items-center gap-0 text-sm font-medium after:absolute after:inset-y-0 after:w-1/2 after:rounded-full after:shadow-xs after:transition-[translate,box-shadow] after:duration-300 after:ease-[cubic-bezier(0.16,1,0.3,1)] has-focus-visible:after:ring-[3px] data-[state=percentage]:after:translate-x-0 data-[state=amount]:after:translate-x-full"
                          data-state={taxToggle}
                        >
                          <label className=" text-skin-primary group-data-[state=amount]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-4 whitespace-nowrap transition-colors select-none">
                            <span>
                              <span className="group-data-[state=amount]:text-muted-foreground/70 transition-colors group-data-[state=percentage]:text-skin-primary">
                                %
                              </span>
                            </span>
                            <RadioGroupItem
                              id={`percentage`}
                              value="percentage"
                              className="sr-only"
                            />
                          </label>
                          <label className="group-data-[state=percentage]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-4 whitespace-nowrap transition-colors select-none">
                            <span>
                              <span className="group-data-[state=percentage]:text-muted-foreground/70 transition-colors group-data-[state=amount]:text-skin-primary">
                                $
                              </span>
                            </span>
                            <RadioGroupItem
                              id={`amount`}
                              value="amount"
                              className="sr-only"
                            />
                          </label>
                        </RadioGroup>
                      </div>
                    </div>
                    {taxToggle === "percentage" ? (
                      <Controller
                        control={form.control}
                        name="taxPercentage"
                        render={({ field }) => {
                          return <NumberInput {...field} type="percentage" />;
                        }}
                      />
                    ) : (
                      <Controller
                        control={form.control}
                        name="taxAmount"
                        render={({ field }) => {
                          return <NumberInput {...field} type="currency" />;
                        }}
                      />
                    )}
                  </div>
                </div>
                {form.formState.errors.taxPercentage && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.taxPercentage.message}
                  </p>
                )}
                {form.formState.errors.taxAmount && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.taxAmount.message}
                  </p>
                )}
              </div>

              {(hasCCFeature || formValues.enableCardPayment !== undefined) && (
                <Controller
                  control={form.control}
                  name="enableCardPayment"
                  render={({ field }) => (
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => field.onChange(!field.value)}
                        className={cn(
                          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                          field.value ? "bg-green-500" : "bg-gray-200",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                            field.value ? "translate-x-6" : "translate-x-1",
                          )}
                        />
                      </button>
                      <span className="text-sm text-gray-700">
                        Enable card payments for this invoice.
                      </span>
                      <HoverCard openDelay={100}>
                        <HoverCardTrigger asChild>
                          <button type="button" className="text-gray-400 hover:text-gray-600">
                            <Info className="w-4 h-4" />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-72 text-xs text-gray-600" side="top">
                          {field.value
                            ? "Customers can choose to pay by card or ACH. Turn off to allow ACH only."
                            : "Card payments are disabled. Customers can only pay via ACH."}
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                  )}
                />
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <Textarea {...form.register("notes")} />
                {form.formState.errors.notes && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.notes.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 px-4 md:px-0">
            <EditSendButton
              form={form}
              loading={
                form.formState.isSubmitting ||
                updateInvoiceMutation.isLoading ||
                sendingInvoice
              }
              isSentInvoice={isSentInvoice}
            />
          </div>

          {/* Confirmation Dialog */}
          <AlertDialog
            open={shouldSendInvoice}
            onOpenChange={(value) => setShouldSendInvoice(value)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Double checking...</AlertDialogTitle>
                <AlertDialogDescription>
                  After sending, edits update the original invoice. Customers
                  will only see the latest version. Review all details before
                  submitting.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button
                  disabled={
                    form.formState.isSubmitting ||
                    updateInvoiceMutation.isLoading ||
                    sendingInvoice
                  }
                  type="button"
                  className="bg-green-500 hover:bg-green-500/80"
                  onClick={() => {
                    const invoiceData = summary.getPayloadData();
                    updateInvoiceMutation.mutate(
                      //@ts-ignore
                      {
                        id: invoiceId,
                        ...invoiceData,
                      },
                      {
                        onSuccess: async () => {
                          setSendingInvoice(true);
                          try {
                            await sendInvoiceToCustomer({
                              userId,
                              invoiceId: invoiceId,
                            });
                            toast.success(
                              "Your invoice was updated successfully.",
                            );
                          } catch (error: any) {
                            if (error?.response?.status === 504) {
                              toast.success("Your invoice has been sent.");
                            } else {
                              toast.success(
                                "Your invoice was updated but it failed to send. If not scheduled, try resending the invoice.",
                              );
                            }
                          } finally {
                            queryClient.invalidateQueries(invoiceQueryKeys.all);
                            setShouldSendInvoice(false);
                            setSendingInvoice(false);
                            history.push("/invoices");
                          }
                        },
                      },
                    );
                  }}
                >
                  {form.formState.isSubmitting ||
                  updateInvoiceMutation.isLoading ||
                  sendingInvoice ? (
                    <div className="flex items-center gap-x-2">
                      Updating Invoice
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </form>

        {/* Create Contact Drawer */}
        <CreateContactDrawer
          open={addContact}
          handleClose={() => setAddContact(false)}
          // @ts-ignore
          onSuccess={(contact: any) => {
            queryClient.invalidateQueries(["contacts"]);
            const selected = InvoiceToSchema.parse({
              ...contact.contact_info,
              contact_id: contact.contact_id,
            });
            form.setValue("invoiceTo", selected, { shouldValidate: true });
          }}
        />
      </main>

      {/* Always show preview on large screens, conditionally on small screens */}
      {(!isSmallScreen || isPreviewOpen) && (
        <aside
          className="sticky top-8 col-span-2 shrink-0 hidden lg:block"
          style={{ display: isSmallScreen ? "none" : undefined }}
        >
          <div className="overflow-hidden rounded-lg bg-gray-100 h-full">
            <div className="relative px-4 py-5 sm:p-6 2xl:p-10 h-full">
              <IframeInvoicePreview
                formData={formValues}
                zoomLevel={zoomLevel}
                showZoomControls={false}
              />
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

const getAllPublishingOptions = () =>
  [
    {
      name: "Update Draft",
      description: "Save changes to this invoice draft.",
      status: "draft",
      deliveryMethod: null,
    },
    {
      name: "Update & Send By Email",
      description: "Save changes and send the invoice to the contact by email.",
      status: "send",
      deliveryMethod: InvoiceDeliveryMethod.EMAIL,
    },
    {
      name: "Update & Send By SMS",
      description: "Save changes and send the invoice to the contact by sms.",
      status: "send",
      deliveryMethod: InvoiceDeliveryMethod.SMS,
    },
    {
      name: "Update & Send By Email And SMS",
      description:
        "Save changes and send the invoice to the contact by email and sms.",
      status: "send",
      deliveryMethod: InvoiceDeliveryMethod.ALL,
    },
  ] as const;

interface EditSendButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "onClick" | "form"
  > {
  form: UseFormReturn<CreateInvoiceFormData>;
  loading: boolean;
  isSentInvoice?: boolean;
}

function EditSendButton({
  form,
  loading,
  disabled,
  isSentInvoice = false,
  ...props
}: EditSendButtonProps) {
  const values = form.watch();
  const contactPhone = values.invoiceTo?.phone;
  const hasPhoneNumber = contactPhone && contactPhone.trim().length > 0;

  // Filter options based on invoice status and contact phone availability
  const publishingOptions = getAllPublishingOptions().filter((option) => {
    // Filter out "Update Draft" option for sent invoices
    if (isSentInvoice && option.status === "draft") {
      return false;
    }

    // Filter out SMS options when contact has no phone number
    if (
      !hasPhoneNumber &&
      (option.deliveryMethod === InvoiceDeliveryMethod.SMS ||
        option.deliveryMethod === InvoiceDeliveryMethod.ALL)
    ) {
      return false;
    }

    return true;
  });

  const selected =
    publishingOptions.find(
      (option) =>
        option.status === values.status &&
        option.deliveryMethod === values.deliveryMethod,
    ) || publishingOptions[0];

  return (
    <div className="divide-primary-foreground/30 inline-flex divide-x rounded-md shadow-xs rtl:space-x-reverse">
      <Button
        type="submit"
        disabled={loading || disabled}
        className="rounded-none shadow-none first:rounded-s-md last:rounded-e-md focus-visible:z-10"
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          selected?.name
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="rounded-none shadow-none first:rounded-s-md last:rounded-e-md focus-visible:z-10"
            size="icon"
            aria-label="Options"
          >
            <ChevronDownIcon size={16} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="max-w-64 md:max-w-xs"
          side="bottom"
          sideOffset={4}
          align="end"
        >
          {publishingOptions.map((option) => (
            <DropdownMenuItem
              key={option.name}
              className="items-start [&>span]:pt-1.5"
              onClick={() => {
                form.setValue("status", option.status);
                form.setValue("deliveryMethod", option.deliveryMethod);
              }}
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{option.name}</span>
                <span className="text-muted-foreground text-xs">
                  {option.description}
                </span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
